import { useEffect, useState } from 'react';
import { Play, Square, Camera, CheckCircle2 } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ReceiptPicker } from '../expenses/ReceiptPicker';
import { toLocalInputValue, fromLocalInputValue, cobraConTarifa, faltaManifiesto } from '../../utils/trips';
import { parseCantidad, formatearCantidad } from '../../utils/finance';
import type { Trip } from '../../types/trip';
import { NOTA_PESO_PROVISIONAL, type MomentoViaje } from '../../utils/tripMoment';


// #190 — Iniciar/Finalizar estampaban SIEMPRE `now()`, así que no había forma
// de registrar un viaje ya realizado: declarar una salida/llegada pasada chocaba
// con la ventana y el core devolvía "la ventana del viaje termina antes de
// empezar". El contrato ya aceptaba `at` en `/start` y `/finish`; este modal lo
// expone. Por defecto el campo arranca en "ahora" (el caso normal: se pulsa
// cuando ocurre); el supervisor puede retroceder la hora para hacer backfill.
//
// Retrodatar es solo de Administrador/Supervisor (`canBackdate`): un Operador
// solo registra "ahora", sin editar la hora, para que no declare salidas o
// llegadas que no ocurrieron. Con `canBackdate` en false no se envía `at`, así
// que el instante lo pone el servidor (`now()`), a prueba del reloj del cliente.
//
// FE-34 (ADR-032) — si el viaje cobra con tarifa, INICIAR pide además el peso
// de carga del manifiesto (el valor «Red:»), el nº del manifiesto y la foto; y
// FINALIZAR no deja cerrar sin la foto si todavía no la tiene (§4.2). Lo que
// este modal NO enseña, a propósito, es dinero: el Operador no ve tarifas ni
// importes (#225), y el total propuesto se lo calcula el core al crear el
// ingreso. El Supervisor lo ve después, en Ingresos.
interface TripMomentModalProps {
  isOpen: boolean;
  accion: 'iniciar' | 'finalizar';
  trip: Trip | null;
  isLoading?: boolean;
  error?: string | null;
  // Solo Administrador/Supervisor pueden elegir una hora pasada. El Operador no.
  canBackdate?: boolean;
  onConfirm: (datos: MomentoViaje) => void;
  onClose: () => void;
}

function formatInstant(iso: string): string {
  return new Intl.DateTimeFormat('es-CO', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}

export function TripMomentModal({ isOpen, accion, trip, isLoading, error, canBackdate = true, onConfirm, onClose }: TripMomentModalProps) {
  const [value, setValue] = useState('');
  const [peso, setPeso] = useState('');
  const [provisional, setProvisional] = useState(false);
  const [referencia, setReferencia] = useState('');
  const [foto, setFoto] = useState<File | null>(null);

  // Al abrir, el campo arranca en "ahora" y lo demás en blanco. Depende de
  // `trip?.id` y `accion` para re-sembrarse si se encadena una acción con otra
  // sin cerrar.
  useEffect(() => {
    if (!isOpen) return;
    setValue(toLocalInputValue(new Date().toISOString()));
    setPeso('');
    setProvisional(false);
    setReferencia('');
    setFoto(null);
  }, [isOpen, trip?.id, accion]);

  if (!trip) return null;
  const esIniciar = accion === 'iniciar';
  const conTarifa = cobraConTarifa(trip);
  // Al iniciar se PIDE la foto; al finalizar se EXIGE si aún no está.
  const pedirManifiesto = conTarifa && (esIniciar || faltaManifiesto(trip));
  const exigeManifiesto = !esIniciar && faltaManifiesto(trip);

  const pesoCanonico = peso.trim() ? parseCantidad(peso) : null;
  const pesoInvalido = esIniciar && conTarifa && peso.trim() !== '' && !pesoCanonico;

  const confirmar = () => {
    const datos: MomentoViaje = { at: canBackdate ? fromLocalInputValue(value) : undefined };
    if (esIniciar && conTarifa) {
      datos.quantity = pesoCanonico ?? undefined;
      datos.reference = referencia.trim() || undefined;
      datos.notes = provisional ? NOTA_PESO_PROVISIONAL : undefined;
    }
    if (pedirManifiesto) datos.manifiesto = foto;
    onConfirm(datos);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${esIniciar ? 'Iniciar' : 'Finalizar'} viaje · ${trip.vehiclePlate}`}
      size={pedirManifiesto ? 'md' : 'sm'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button
            variant={esIniciar ? 'primary' : 'success'}
            leftIcon={esIniciar ? <Play size={14} /> : <Square size={14} />}
            onClick={confirmar}
            isLoading={isLoading}
            disabled={(canBackdate && !value) || pesoInvalido || (exigeManifiesto && !foto)}
          >
            {esIniciar ? 'Iniciar' : 'Finalizar'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {canBackdate ? (
          <>
            <Input
              label={esIniciar ? 'Hora de salida real' : 'Hora de llegada real'}
              type="datetime-local"
              value={value}
              onChange={e => setValue(e.target.value)}
            />
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
              Déjala en la hora actual para registrar que ocurre ahora, o elige una hora pasada
              para registrar un viaje ya realizado.
            </p>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
            Se registrará con la hora actual.
          </p>
        )}

        {conTarifa && (
          <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)' }}>
            {trip.customerName ?? 'Cliente'}{trip.tariffName ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> · {trip.tariffName}</span> : null}
          </p>
        )}

        {esIniciar && conTarifa && (
          <>
            <Input
              label="Peso de carga del manifiesto (valor «Red:»)"
              value={peso}
              onChange={e => setPeso(e.target.value)}
              inputMode="decimal"
              placeholder="1.740"
              error={pesoInvalido ? 'No se entiende ese peso. Prueba con 1.740 o 1740,5.' : undefined}
            />
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
              {pesoCanonico
                ? <>Se declararán <strong>{formatearCantidad(pesoCanonico)}</strong> en la unidad de la tarifa. Si te equivocas, el supervisor lo corrige después.</>
                : <>El peso NETO que trae el manifiesto, en la unidad de la tarifa. Es obligatorio cuando la tarifa cobra por peso; si no lo conoces aún, escribe uno aproximado y márcalo provisional.</>}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={provisional} onChange={e => setProvisional(e.target.checked)} />
              Peso provisional: todavía no tengo el tiquete definitivo
            </label>
            <Input
              label="Nº del manifiesto (opcional)"
              value={referencia}
              onChange={e => setReferencia(e.target.value)}
              placeholder="MAN-0042"
            />
          </>
        )}

        {pedirManifiesto && (
          <>
            <ReceiptPicker
              file={foto}
              onChange={setFoto}
              disabled={isLoading}
              label={exigeManifiesto ? 'Foto del manifiesto (obligatoria para finalizar)' : 'Foto del manifiesto'}
              ocultarOpcional
            />
            <p style={{ margin: 0, fontSize: '11px', color: exigeManifiesto ? '#F59E0B' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Camera size={12} />
              {exigeManifiesto
                ? 'Este viaje no tiene todavía la foto del manifiesto: no se puede finalizar sin ella.'
                : 'Puedes ponerla ahora o más tarde desde la tarjeta del viaje, pero hace falta para finalizarlo.'}
            </p>
          </>
        )}

        {!esIniciar && conTarifa && trip.hasManifest && (
          <p style={{ margin: 0, fontSize: '11px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 700 }}>
            <CheckCircle2 size={12} /> Manifiesto adjunto.
          </p>
        )}

        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)' }}>
          Programado: {formatInstant(trip.scheduledFrom)} → {formatInstant(trip.scheduledTo)}
          {trip.actualFrom && <> · Salida real: {formatInstant(trip.actualFrom)}</>}
        </p>
        {error && <p style={{ margin: 0, fontSize: '12px', color: 'var(--danger, #dc2626)' }}>{error}</p>}
      </div>
    </Modal>
  );
}
